import {
  EnhancedSeoHead,
  generateProviderDetailSchemas,
  NotFoundPage,
  ProviderDetailPage,
} from "@appcaire/shared/seo";
import {
  useGetConsumerProviderWithDetailsQuery,
  useGetMunicipalityQuery,
} from "@appcaire/graphql";
import { useNavigate, useParams } from "react-router-dom";
import { useStockholmEventTracking } from "../lib/analytics";
import { primaryDistrictLabel } from "../lib/districtFaq";
import { localProviderCanonicalsEnabled } from "../lib/env";
import { providerPageTitle } from "../lib/seo-text";
import {
  OFFICIAL_STOCKHOLM_CHOOSER_URL,
  OFFICIAL_STOCKHOLM_HOME_CARE_URL,
  OFFICIAL_STOCKHOLM_QUALITY_URL,
  SITE_ORIGIN,
  STOCKHOLM_SLUG,
} from "../lib/site";
import {
  MAX_SHORTLIST_UNITS,
  useShortlist,
} from "../shortlist/ShortlistContext";
import { DataUnavailable, PageLoading } from "./PageStates";

export default function ProviderPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const trackStockholmEvent = useStockholmEventTracking();
  const shortlist = useShortlist();
  const providerQuery = useGetConsumerProviderWithDetailsQuery({
    variables: { slug, municipalitySlug: STOCKHOLM_SLUG },
    skip: !slug,
  });
  const municipalityQuery = useGetMunicipalityQuery({
    variables: { slug: STOCKHOLM_SLUG },
  });

  const localCanonical = `${SITE_ORIGIN}/utforare/${encodeURIComponent(slug)}`;
  if (providerQuery.loading || municipalityQuery.loading) {
    return (
      <>
        <EnhancedSeoHead
          siteKey="stockholm"
          title="Hemtjänstutförare i Stockholm"
          description="Information om en hemtjänstutförare i Stockholm, från officiella källor."
          canonicalUrl={
            localProviderCanonicalsEnabled()
              ? localCanonical
              : `https://hemtjanstguide.se/anordnare/${encodeURIComponent(slug)}?kommun=stockholm`
          }
        />
        <PageLoading label="Hämtar utförarens aktuella uppgifter…" />
      </>
    );
  }
  if (providerQuery.error || municipalityQuery.error) {
    return (
      <DataUnavailable
        path={`/utforare/${encodeURIComponent(slug)}`}
        title="Utförarinformationen är tillfälligt otillgänglig"
      />
    );
  }

  const provider = providerQuery.data?.consumerProviderWithDetails ?? null;
  const municipality = municipalityQuery.data?.municipality ?? null;
  if (
    !provider ||
    !provider.presence ||
    provider.presence.municipality.slug !== STOCKHOLM_SLUG
  ) {
    return (
      <NotFoundPage
        title="Utföraren hittades inte i Stockholm"
        description="Den här länken motsvarar inte en aktiv hemtjänstutförare i Stockholms lista."
        navigationLinks={[
          { label: "Hitta hemtjänst", to: "/hitta-hemtjanst" },
          { label: "Alla stadsdelar", to: "/stadsdelar" },
          { label: "Startsidan", to: "/" },
        ]}
      />
    );
  }

  const providerName = provider.displayName || provider.name;
  const selfCanonical = localProviderCanonicalsEnabled();
  const canonicalUrl = selfCanonical
    ? localCanonical
    : `https://hemtjanstguide.se/anordnare/${encodeURIComponent(provider.slug)}?kommun=stockholm`;
  const primaryDistrict = primaryDistrictLabel(
    provider.presence.geographicAreas,
  );
  const title = providerPageTitle(providerName, primaryDistrict);
  const description = primaryDistrict
    ? `${providerName} är en hemtjänstutförare i ${primaryDistrict}, Stockholm. Se stadsdelar, kontakt, språk, kompetens och kvalitet från officiella källor.`
    : `${providerName} är en hemtjänstutförare i Stockholm. Se vilka stadsdelar de arbetar i, kontakt, språk, kompetens och kvalitet från officiella källor.`;
  const saved = shortlist.isSaved(provider.id);
  const compared = saved;
  const saveDisabled =
    !saved && shortlist.providerIds.length >= MAX_SHORTLIST_UNITS;
  const compareDisabled = saveDisabled;
  const saveDisabledReason =
    "Du kan spara högst tre utförare. Ta bort en för att välja en annan.";
  const chooserUrl =
    municipality?.providerListSource?.url ?? OFFICIAL_STOCKHOLM_CHOOSER_URL;
  const sourceUrl = chooserUrl;
  const marketUrl = `https://sverigeshemtjanst.se/anordnare/${encodeURIComponent(provider.slug)}?kommun=stockholm`;
  const ownerUrl = `https://sverigeshemtjanst.se/mittcaire?provider=${encodeURIComponent(provider.id)}&municipality=stockholm&source=hemtjanstistockholm`;
  const providerSchemas = generateProviderDetailSchemas({
    canonicalUrl,
    description,
    provider,
    municipalityName: "Stockholms stad",
    municipalities: ["Stockholms stad"],
    breadcrumbs: [
      { name: "Hem", url: SITE_ORIGIN },
      {
        name: "Hitta hemtjänst",
        url: `${SITE_ORIGIN}/hitta-hemtjanst`,
      },
      { name: providerName, url: canonicalUrl },
    ],
  });

  const goToCompare = () => {
    const ids = saved
      ? shortlist.providerIds
      : [...shortlist.providerIds, provider.id].slice(0, MAX_SHORTLIST_UNITS);
    if (!saved) shortlist.toggle(provider.id);
    navigate(`/jamfor?compare=${encodeURIComponent(ids.join(","))}`);
  };

  return (
    <>
      <EnhancedSeoHead
        siteKey="stockholm"
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        keywords={[
          `${providerName} hemtjänst`,
          `${providerName} Stockholm`,
          ...(primaryDistrict
            ? [
                `${providerName} ${primaryDistrict}`,
                `hemtjänst ${primaryDistrict}`,
              ]
            : []),
          "hemtjänstutförare Stockholm",
        ]}
        ogType="profile"
        contentClassification="Consumer provider profile"
        targetAudience="seniors, relatives, Stockholm residents"
        schemaMarkup={providerSchemas}
      />
      <ProviderDetailPage
        provider={provider}
        municipalityName="Stockholms stad"
        municipalitySlug={STOCKHOLM_SLUG}
        municipalityRankScope={{ slug: STOCKHOLM_SLUG }}
        municipalityRankDisplayName="Stockholms stad"
        backLink="/hitta-hemtjanst"
        backLabel="Tillbaka till alla utförare"
        variant="consumer"
        contactFormEnabled
        contactSource="hemtjanstistockholm-provider"
        officialChooserUrl={chooserUrl}
        consumerConfig={{
          // Keep the shared optional question field available to Stockholm
          // visitors. It is bounded and carries the same privacy warning as
          // every other consumer inquiry form; the value is never sent to
          // analytics.
          allowContactMessage: true,
          officialSourceName: "Stockholms stad",
          showCaireIndex: true,
          caireIndexMethodologyHref:
            "https://sverigeshemtjanst.se/topplistor/metodik",
          onCaireIndexMethodologyClick: () =>
            trackStockholmEvent("seo_funnel_click", {
              provider_id: provider.id,
              source_surface: "provider",
              target_path: "/topplistor/metodik",
            }),
          officialLinks: [
            { label: "Stockholms officiella utförarval", href: sourceUrl },
            {
              label: "Välj eller ansök om hemtjänst",
              href: OFFICIAL_STOCKHOLM_HOME_CARE_URL,
            },
            {
              label: "Stockholms kvalitetsinformation",
              href:
                municipality?.qualityDataUrl ?? OFFICIAL_STOCKHOLM_QUALITY_URL,
            },
          ],
          marketAnalysisUrl: marketUrl,
          profileOwnerUrl: ownerUrl,
          saved,
          compared,
          saveDisabled,
          saveDisabledReason,
          compareDisabled,
          onSave: () => shortlist.toggle(provider.id),
          onCompare: goToCompare,
          onMarketAnalysisClick: () =>
            trackStockholmEvent("seo_funnel_click", {
              provider_id: provider.id,
              source_surface: "provider",
              target_path: `/anordnare/${provider.slug}`,
            }),
          onProfileOwnerClick: () =>
            trackStockholmEvent("seo_funnel_click", {
              provider_id: provider.id,
              source_surface: "provider",
              target_path: "/mittcaire",
            }),
        }}
      />
    </>
  );
}
